// client-logger.ts — Browser-compatible logging with same API as server pino logger
// Console-based logging with category support and data sanitization
// Follows logging philosophy from CLAUDE.md for instructive debugging

import {
  DISABLED_LOG_CATEGORIES,
  getModuleLogLevel,
  LOG_LEVEL_CLIENT_DEFAULT,
  LOG_REDACTED_TEXT,
  LOG_SENSITIVE_FIELDS,
} from "@game/config";

const SENSITIVE_FIELDS = LOG_SENSITIVE_FIELDS;
const REDACTED = LOG_REDACTED_TEXT;
const LOG_LEVEL = LOG_LEVEL_CLIENT_DEFAULT;
const LOG_CATEGORIES: string[] = [];

const categoryLevels: Record<string, string> = {};
const moduleLogLevels: Record<string, string> = {};
const clientModuleNames = [
  "rendering",
  "input",
  "networking",
  "ui",
  "game",
  "audio",
  "lobby",
  "metrics",
  "toasts",
  "modals",
  "websocket",
];

clientModuleNames.forEach((module) => {
  try {
    const localStorageKey = `LOG_LEVEL_${module.toUpperCase()}`;
    const localStorageValue = localStorage.getItem(localStorageKey);
    if (localStorageValue) {
      moduleLogLevels[module] = localStorageValue;
      console.log(
        `🔧 Client debug override: ${module} log level set to ${localStorageValue} via localStorage`,
      );
    }
  } catch {}
});

let websocketConnection: WebSocket | null = null;

export function setLogForwardingWebSocket(ws: WebSocket | null): void {
  websocketConnection = ws;
}

function forwardClientLog(
  level: string,
  message: string,
  context: object,
): void {
  if (websocketConnection?.readyState === WebSocket.OPEN) {
    try {
      websocketConnection.send(
        JSON.stringify({
          type: "client_log",
          level,
          message: `[client] ${message}`,
          context: {
            ...context,
            timestamp: Date.now(),
            userAgent: navigator.userAgent,
          },
        }),
      );
    } catch (error) {
      console.debug("Failed to forward log to server:", error);
    }
  }
}

export function sanitizeLogData(obj: any): any {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  const sanitized: any = Array.isArray(obj) ? [] : {};

  for (const key in obj) {
    const lowerKey = key.toLowerCase();
    if (SENSITIVE_FIELDS.some((field) => lowerKey.includes(field))) {
      sanitized[key] = REDACTED;
    } else if (typeof obj[key] === "object") {
      sanitized[key] = sanitizeLogData(obj[key]);
    } else {
      sanitized[key] = obj[key];
    }
  }

  return sanitized;
}

function createBrowserLogger(category: string, level = LOG_LEVEL) {
  const levelNumbers: Record<string, number> = {
    silent: 70,
    fatal: 60,
    error: 50,
    warn: 40,
    test: 35,
    info: 30,
    debug: 20,
    trace: 10,
  };

  const currentLevel = levelNumbers[level] || 30;
  const prefix = `[${category}]`;

  return {
    level,
    category,
    trace: (msg: string, ...args: any[]) => {
      if (currentLevel <= 10) {
        const sanitizedArgs = args.map((arg) =>
          typeof arg === "object" && arg !== null ? sanitizeLogData(arg) : arg,
        );
        console.debug(`${prefix}[TRACE]`, msg, ...sanitizedArgs);
      }
    },
    debug: (msg: string, ...args: any[]) => {
      if (currentLevel <= 20) {
        const sanitizedArgs = args.map((arg) =>
          typeof arg === "object" && arg !== null ? sanitizeLogData(arg) : arg,
        );
        console.debug(`${prefix}[DEBUG]`, msg, ...sanitizedArgs);
      }
    },
    info: (msg: string, ...args: any[]) => {
      if (currentLevel <= 30) {
        const sanitizedArgs = args.map((arg) =>
          typeof arg === "object" && arg !== null ? sanitizeLogData(arg) : arg,
        );
        console.info(`${prefix}[INFO]`, msg, ...sanitizedArgs);
      }
    },
    warn: (msg: string, ...args: any[]) => {
      if (currentLevel <= 40) {
        const sanitizedArgs = args.map((arg) =>
          typeof arg === "object" && arg !== null ? sanitizeLogData(arg) : arg,
        );
        console.warn(`${prefix}[WARN]`, msg, ...sanitizedArgs);
      }
    },
    error: (msg: string, ...args: any[]) => {
      if (currentLevel <= 50) {
        const sanitizedArgs = args.map((arg) =>
          typeof arg === "object" && arg !== null ? sanitizeLogData(arg) : arg,
        );
        console.error(`${prefix}[ERROR]`, msg, ...sanitizedArgs);
      }
    },
    fatal: (msg: string, ...args: any[]) => {
      if (currentLevel <= 60) {
        const sanitizedArgs = args.map((arg) =>
          typeof arg === "object" && arg !== null ? sanitizeLogData(arg) : arg,
        );
        console.error(`${prefix}[FATAL]`, msg, ...sanitizedArgs);
      }
    },
    test: (msg: string, ...args: any[]) => {
      if (currentLevel <= 35) {
        const sanitizedArgs = args.map((arg) =>
          typeof arg === "object" && arg !== null ? sanitizeLogData(arg) : arg,
        );
        console.log(
          `%c${prefix}[TEST]`,
          "background: blue; color: white; font-weight: bold;",
          msg,
          ...sanitizedArgs,
        );
      }
    },
  };
}

const loggerCache: Map<string, any> = new Map();

export interface ClientModuleLogger {
  trace: (msg: string, context?: object) => void;
  debug: (msg: string, context?: object) => void;
  info: (msg: string, context?: object) => void;
  warn: (msg: string, context?: object) => void;
  error: (msg: string, context?: object) => void;
  fatal: (msg: string, context?: object) => void;
  test: (msg: string, context?: object) => void;
}

export function createLogger(category: string): any {
  if (DISABLED_LOG_CATEGORIES.includes(category as any)) {
    return createBrowserLogger(category, "silent");
  }

  if (LOG_CATEGORIES.length > 0 && !LOG_CATEGORIES.includes(category)) {
    return createBrowserLogger(category, "silent");
  }

  if (loggerCache.has(category)) {
    return loggerCache.get(category)!;
  }

  const level = categoryLevels[category] || LOG_LEVEL;
  const logger = createBrowserLogger(category, level);

  loggerCache.set(category, logger);
  return logger;
}

export function createClientModuleLogger(
  moduleName: string,
  logLevel?: string,
): ClientModuleLogger {
  if (DISABLED_LOG_CATEGORIES.includes(moduleName as any)) {
    return createSilentClientModuleLogger();
  }

  const level =
    logLevel || moduleLogLevels[moduleName] || getModuleLogLevel(moduleName);
  const baseLogger = createBrowserLogger(moduleName, level);

  return {
    trace: (msg: string, context?: object) => {
      const enrichedContext = {
        ...context,
        module: moduleName,
        timestamp: Date.now(),
      };
      baseLogger.trace(`[${moduleName}] ${msg}`, enrichedContext);
      forwardClientLog("trace", msg, enrichedContext);
    },
    debug: (msg: string, context?: object) => {
      const enrichedContext = {
        ...context,
        module: moduleName,
        timestamp: Date.now(),
      };
      baseLogger.debug(`[${moduleName}] ${msg}`, enrichedContext);
      forwardClientLog("debug", msg, enrichedContext);
    },
    info: (msg: string, context?: object) => {
      const enrichedContext = {
        ...context,
        module: moduleName,
        timestamp: Date.now(),
      };
      baseLogger.info(`[${moduleName}] ${msg}`, enrichedContext);
      forwardClientLog("info", msg, enrichedContext);
    },
    warn: (msg: string, context?: object) => {
      const enrichedContext = {
        ...context,
        module: moduleName,
        timestamp: Date.now(),
      };
      baseLogger.warn(`[${moduleName}] ${msg}`, enrichedContext);
      forwardClientLog("warn", msg, enrichedContext);
    },
    error: (msg: string, context?: object) => {
      const enrichedContext = {
        ...context,
        module: moduleName,
        timestamp: Date.now(),
      };
      baseLogger.error(`[${moduleName}] ${msg}`, enrichedContext);
      forwardClientLog("error", msg, enrichedContext);
    },
    fatal: (msg: string, context?: object) => {
      const enrichedContext = {
        ...context,
        module: moduleName,
        timestamp: Date.now(),
      };
      baseLogger.fatal(`[${moduleName}] ${msg}`, enrichedContext);
      forwardClientLog("fatal", msg, enrichedContext);
    },
    test: (msg: string, context?: object) => {
      const enrichedContext = {
        ...context,
        module: moduleName,
        timestamp: Date.now(),
      };
      baseLogger.test(`[${moduleName}] ${msg}`, enrichedContext);
      forwardClientLog("test", msg, enrichedContext);
    },
  };
}

function createSilentClientModuleLogger(): ClientModuleLogger {
  const noop = () => {};
  return {
    trace: noop,
    debug: noop,
    info: noop,
    warn: noop,
    error: noop,
    fatal: noop,
    test: noop,
  };
}

export const clientLogger = createLogger("CLIENT");
export const appLogger = createLogger("APP");
export const uiLogger = createLogger("UI");
export const gameLogger = createLogger("GAME");
export const wsLogger = createLogger("WEBSOCKET");
export const startupLogger = createLogger("STARTUP");

export const renderingModuleLogger = createClientModuleLogger("rendering");
export const inputModuleLogger = createClientModuleLogger("input");
export const networkingClientModuleLogger =
  createClientModuleLogger("networking");
export const uiModuleLogger = createClientModuleLogger("ui");
export const gameClientModuleLogger = createClientModuleLogger("game");
export const audioModuleLogger = createClientModuleLogger("audio");
export const lobbyClientModuleLogger = createClientModuleLogger("lobby");
export const metricsClientModuleLogger = createClientModuleLogger("metrics");
export const toastsModuleLogger = createClientModuleLogger("toasts");
export const modalsModuleLogger = createClientModuleLogger("modals");
export const websocketModuleLogger = createClientModuleLogger("websocket");

export interface ClientValidationResult {
  status: "success" | "error";
  timeMs: number;
  details?: any;
  error?: string;
}

export async function validateClientStartup(): Promise<boolean> {
  startupLogger.info("🚀 Starting client validation sequence");

  try {
    startupLogger.info("🔌 Validating WebSocket connectivity...");
    const wsResult = await validateWebSocketConnectivity();
    if (wsResult.status === "success") {
      startupLogger.info("🔌 WebSocket validation: success", {
        connectTime: wsResult.timeMs,
        details: wsResult.details,
      });
    } else {
      startupLogger.error("🔌 WebSocket validation failed", {
        connectTime: wsResult.timeMs,
        error: wsResult.error,
      });
      return false;
    }

    startupLogger.info("🖼️ Validating HTML5 Canvas...");
    const canvasResult = await validateCanvas();
    if (canvasResult.status === "success") {
      startupLogger.info("🖼️ Canvas validation: success", {
        initTime: canvasResult.timeMs,
        details: canvasResult.details,
      });
    } else {
      startupLogger.error("🖼️ Canvas validation failed", {
        initTime: canvasResult.timeMs,
        error: canvasResult.error,
      });
      return false;
    }

    startupLogger.info("⚛️ Validating React components...");
    const reactResult = await validateReactComponents();
    if (reactResult.status === "success") {
      startupLogger.info("⚛️ React validation: success", {
        initTime: reactResult.timeMs,
        details: reactResult.details,
      });
    } else {
      startupLogger.error("⚛️ React validation failed", {
        initTime: reactResult.timeMs,
        error: reactResult.error,
      });
      return false;
    }

    startupLogger.info("📦 Validating client assets...");
    const assetsResult = await validateAssets();
    if (assetsResult.status === "success") {
      startupLogger.info("📦 Assets validation: success", {
        loadTime: assetsResult.timeMs,
        details: assetsResult.details,
      });
    } else {
      startupLogger.error("📦 Assets validation failed", {
        loadTime: assetsResult.timeMs,
        error: assetsResult.error,
      });
      return false;
    }

    startupLogger.info("✅ Client startup validation complete");
    return true;
  } catch (error) {
    startupLogger.error("❌ Client startup validation failed with exception", {
      error: error instanceof Error ? error.message : "Unknown error",
      stack: error instanceof Error ? error.stack : undefined,
    });
    return false;
  }
}

async function validateWebSocketConnectivity(): Promise<ClientValidationResult> {
  const startTime = Date.now();

  try {
    if (typeof WebSocket === "undefined") {
      throw new Error("WebSocket not available in this environment");
    }

    const wsUrl = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}`;

    return {
      status: "success",
      timeMs: Date.now() - startTime,
      details: {
        wsSupported: true,
        url: wsUrl,
        readyState: "available",
      },
    };
  } catch (error) {
    return {
      status: "error",
      timeMs: Date.now() - startTime,
      error:
        error instanceof Error ? error.message : "WebSocket validation failed",
    };
  }
}

async function validateCanvas(): Promise<ClientValidationResult> {
  const startTime = Date.now();

  try {
    const canvas = document.createElement("canvas");
    if (!canvas.getContext) {
      throw new Error("HTML5 Canvas not supported");
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Canvas 2D context not available");
    }

    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, 10, 10);

    return {
      status: "success",
      timeMs: Date.now() - startTime,
      details: {
        canvasSupported: true,
        context2d: true,
        testRenderComplete: true,
      },
    };
  } catch (error) {
    return {
      status: "error",
      timeMs: Date.now() - startTime,
      error:
        error instanceof Error ? error.message : "Canvas validation failed",
    };
  }
}

async function validateReactComponents(): Promise<ClientValidationResult> {
  const startTime = Date.now();

  try {
    if (typeof window.React === "undefined") {
      throw new Error("React not loaded");
    }

    if (typeof window.ReactDOM === "undefined") {
      throw new Error("ReactDOM not loaded");
    }

    const uiRoot = document.getElementById("ui-root");
    if (!uiRoot) {
      throw new Error("UI root container not found");
    }

    return {
      status: "success",
      timeMs: Date.now() - startTime,
      details: {
        reactLoaded: true,
        reactDOMLoaded: true,
        uiRootExists: true,
      },
    };
  } catch (error) {
    return {
      status: "error",
      timeMs: Date.now() - startTime,
      error: error instanceof Error ? error.message : "React validation failed",
    };
  }
}

async function validateAssets(): Promise<ClientValidationResult> {
  const startTime = Date.now();

  try {
    const testElement = document.createElement("div");
    testElement.className = "game-ui";
    document.body.appendChild(testElement);

    const styles = window.getComputedStyle(testElement);
    document.body.removeChild(testElement);

    const gameCanvas = document.getElementById("gameCanvas");
    if (!gameCanvas) {
      throw new Error("Game canvas element not found");
    }

    return {
      status: "success",
      timeMs: Date.now() - startTime,
      details: {
        cssLoaded: true,
        gameCanvasExists: true,
        stylesApplied: styles.length > 0,
      },
    };
  } catch (error) {
    return {
      status: "error",
      timeMs: Date.now() - startTime,
      error:
        error instanceof Error ? error.message : "Assets validation failed",
    };
  }
}

export default createLogger("DEFAULT");
